import { IconButton, TableCell, Tooltip } from '@mui/material';
import { TermBond } from '../debt-service-page.tsx';
import moment from 'moment';
import formatter from "../../../tools/formatter.js";
import useDealBuilderResultStore from '../../../stores/deal-builder-result.store.ts';
import { DealBuilder } from '../../services/deal-builder.ts';
import GroupOffIcon from '@mui/icons-material/GroupOff';
import { useDragAndDropContext } from '../bonds-page/drag-and-drop-context.tsx';
import DragAndDropRow from '../bonds-page/drag-and-drop-row.tsx';
import MaturityPicker from '../bonds-page/maturity-picker.tsx';
import { Delete } from '@mui/icons-material';
import React from 'react';
import CurrencyInput from '../bonds-page/currency-input.tsx';

type TermBondPrincipalProps = {
  builder: DealBuilder,
  bond: TermBond,
} & (
  { header: true } |
  { header: false, principalIndex: number }
)

export default function TermBondPrincipalRow(props: TermBondPrincipalProps) {
  const { bond, builder, header } = props;
  const { setTarget, isCurrentSource } = useDragAndDropContext();
  const { setDealBuilderService } = useDealBuilderResultStore();
  const production = builder.production.find(x => x.bond === bond);

  const principal = header ? bond.redemptions.reduce((prev, curr) => prev + curr.amount, 0) : bond.redemptions[props.principalIndex].amount;
  const isDragging = header ? false : isCurrentSource({ bond, subType: "principal", index: props.principalIndex });
  
  function updateTarget(e: React.DragEvent) {
    if (header) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setTarget({ bond, position: "middle", subType: "principal", index: props.principalIndex });
  }

  return (
    <DragAndDropRow
      dragging={isDragging} 
      onDragOver={updateTarget}
      indicator="none"
    >
      <TableCell width="3px" sx={{ p: 0 }}></TableCell>
      <TableCell width="70px">
        {header && (
          <Tooltip title="Ungroup" arrow>
            <IconButton 
              size="small"
              sx={{ 
                color: 'error.main', 
                ml: 0.5,
                '&:hover': { color: 'error.dark' },
              }}
              onClick={async () => {
                builder.ungroupTermBond(bond);
                await builder.updateProduction();
                setDealBuilderService(builder);
                await builder.updateProduction(builder.bonds);
                setDealBuilderService(builder);
              }}
            >
              <GroupOffIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </TableCell>
      <TableCell>
        <MaturityPicker
          format="MM/DD/YYYY"
          value={moment(header ? bond.maturity : bond.redemptions[props.principalIndex].date)}
          firstCouponDate={builder.firstCouponDate}
          disabled={true}
          onChange={async (value) => {
            const oldDate = moment(header ? bond.maturity : bond.redemptions[props.principalIndex].date);
            if (value.isSame(oldDate, "day")) return;
            builder.updateBondDate(bond, value);
            builder.updateDebtService();
            setDealBuilderService(builder);
            await builder.updateProduction(builder.bonds);
            setDealBuilderService(builder);
          }}
        />
      </TableCell>

      <TableCell align="right">
        {header ? (
          formatter.money(principal)
        ) : (
          <CurrencyInput
            className={"switch-db-0"}
            value={bond.redemptions[props.principalIndex].amount}
            size="small"
            startAdornment="$"
            locked={bond.redemptions[props.principalIndex].locked}
            onLockedChange={async (val) => {
              bond.redemptions[props.principalIndex].locked = val;
              builder.updateDebtService();
              setDealBuilderService(builder);
              await builder.updateProduction(builder.bonds);
              setDealBuilderService(builder);
            }}
            onValidChange={async (value) => {
              const newPrincipal = value * 1000;
              const oldPrincipal = bond.redemptions[props.principalIndex].amount;
              if (oldPrincipal === newPrincipal || oldPrincipal === value) return;
              const diff = newPrincipal - oldPrincipal ;
              builder.totalTargetParAmount += diff;
              bond.redemptions[props.principalIndex].amount = newPrincipal;
              bond.redemptions[props.principalIndex].locked = true;
              builder.updateDebtService();
              setDealBuilderService(builder);
              await builder.updateProduction(builder.bonds);
              setDealBuilderService(builder);
            }}
            isValueValid={(value) => {
              const valid = (value * 1000) % builder.denomination === 0;
              if(valid) return { valid: true };
              return { valid: false, error: "The principal must be divisible by the denomination." };
            }}
          />
        )}
      </TableCell>

      <TableCell align="right">
        {header ? (
          <CurrencyInput
            className={"switch-db-1"}
            decimalScale={3}
            disabled={!header}
            value={bond.coupon * 100}
            size="small"
            endAdornment="%"
            locked={bond.lockedCoupon}
            onLockedChange={(val) => {
              bond.lockedCoupon = val;
              builder.updateDebtService();
              setDealBuilderService(builder);
            }}
            onValidChange={async (value) => {
              const diff = bond.coupon - value / 100;
              if (Math.abs(diff) < 0.00001) return;
              bond.coupon = value / 100
              bond.lockedCoupon = true;
              builder.updateDebtService();
              setDealBuilderService(builder);
              await builder.updateProduction(builder.bonds);
              setDealBuilderService(builder);
            }}
            isValueValid={() => ({ valid: true })}
          />
        ) : (
          <CurrencyInput
            className={"switch-db-1"}
            value={bond.redemptions[props.principalIndex].redemptionPrice}
            size="small"
            startAdornment="%"
            locked={undefined}
            onLockedChange={(val) => {
              bond.redemptions[props.principalIndex].locked = val;
            }}
            onValidChange={async (value) => {
              if (bond.redemptions[props.principalIndex].redemptionPrice === value) return;
              bond.redemptions[props.principalIndex].redemptionPrice = value;
              builder.updateDebtService();
              await builder.updateProduction();
              setDealBuilderService(builder);
            }}
            isValueValid={() => ({ valid: true })}
          />
        )}
      </TableCell>

      {!header ? (
        <>
          <TableCell align="right"></TableCell>
          <TableCell align="right"></TableCell>
          <TableCell align="right"></TableCell>
          <TableCell align="right"></TableCell>
          <TableCell align="right"></TableCell>
          <TableCell align="right"></TableCell>
        </>
      ) : !production ? (
        <>
          <TableCell align="right">-</TableCell>
          <TableCell align="right">-</TableCell>
          <TableCell align="right">-</TableCell>
          <TableCell align="right">-</TableCell>
          <TableCell align="right">-</TableCell>
          <TableCell align="right">-</TableCell>
        </>
      ) : (
        <>
          <TableCell align="right">{formatter.percent(production.curve, 3)}</TableCell>

          <TableCell align="right">
            <CurrencyInput
              decimalScale={3}
              className={"switch-db-2"}
              value={production.spread * 100}
              endAdornment="%"
              size="small"
              locked={production.locked}
              onLockedChange={async (val) => {
                production.locked = val;
                setDealBuilderService(builder);
              }}
              onValidChange={async (value) => {
                const diff = production.spread - value / 100;
                if (Math.abs(diff) < 0.00001) return;
                production.locked = true;
                production.spread = value / 100;
                await builder.updateProduction();
                setDealBuilderService(builder);
              }}
              isValueValid={() => ({ valid: true })}
            />
          </TableCell>

          <TableCell align="right">
            <CurrencyInput
              decimalScale={3}
              className={"switch-db-3"}
              value={(production.spread + production.curve) * 100}
              endAdornment="%"
              size="small"
              locked={production.locked}
              onLockedChange={(val) => {
                production.locked = val;
                setDealBuilderService(builder);
              }}
              onValidChange={async (value) => {
                const diff = (value / 100) - production.spread - production.curve;
                if (Math.abs(diff) < 0.00001) return;
                production.locked = true;
                production.spread += diff;
                await builder.updateProduction();
                setDealBuilderService(builder);
              }}
              isValueValid={() => ({ valid: true })}
            />
          </TableCell>

          <TableCell align="right">
            {formatter.percent(production.ytm, 3)}
          </TableCell>

          <TableCell align="right">
            {formatter.money(production.price, true)}
          </TableCell>

          <TableCell align="right">{formatter.money(production.production, false)}</TableCell>
        </>
      )}

      <TableCell align="center">
        {header && (
          <IconButton 
            size="small"
            sx={{ color: 'error.main', '&:hover': { color: 'error.dark' } }}
            onClick={async () => {
              builder.deleteBond(bond);
              builder.updateDebtService();
              await builder.updateProduction();
              setDealBuilderService(builder);
            }}
          >
            <Delete fontSize="small" />
          </IconButton>
        )}
      </TableCell>
    </DragAndDropRow>
  );
};
